"use client"
import Ad from "src/components/ads/Ad";
import Button from "src/components/button/Button";
import withWatchlist from "src/components/filmwebid/withWatchlist";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import OptionsFilmOrSeries from "src/components/streaming/findsomethingtostream/OptionsFilmOrSeries";
import OptionsGenre from "src/components/streaming/findsomethingtostream/OptionsGenre";
import OptionsPeriod from "src/components/streaming/findsomethingtostream/OptionsPeriod";
import OptionsProviders from "src/components/streaming/findsomethingtostream/OptionsProviders";
import StreamingTitleWithProviders from "src/components/streaming/streamingtitlewithproviders/StreamingTitleWithProviders";
import theme from "src/components/themes/DefaultTheme";
import { gql } from "graphql-request";
import { useFilminfo } from "src/lib/client/useFilminfo";
import withScrollRestoration, { SupportsScrollRestoration } from "src/lib/client/withScrollRestoration";
import { StreamingContent, StreamingProvider } from "src/lib/movieinfo/movieinfotypes";
import { FilterAction, filterReducer, IFindSomethingToStreamFilterState, INTIAL_STATE, STREAMING_GENRES } from "src/lib/streaming/FindSomethingToStreamFilter";
import styled from 'src/lib/styles/css';
import { useRouter } from "next/router";
import React from "react";
import { Dispatch, useEffect, useReducer } from "react";

//#region [Props]
type StreamingContentFinderProps = {
	providers: StreamingProvider[];
} & SupportsScrollRestoration;
//#endregion

//#region [Component]


function StreamingContentFinder({ providers, isReadyForScrollRestoration }: StreamingContentFinderProps) {
	const [filterState, filterDispatch] = useReducer(filterReducer, INTIAL_STATE);

	const router = useRouter();

	useEffect(() => {
		parseInitialUrlState(router, providers, filterDispatch);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Update the url on a filter change
		let newUrl: string[] = [];
		if (filterState.genres.length > 0) {
			newUrl.push(`genres=${filterState.genres.map(g => g.substring(0, 3)).join("").toLowerCase()}`);
		}
		if (filterState.providers.length > 0) {
			newUrl.push(`providers=${filterState.providers.map(p => p.name!.substring(0, 4)).join("").toLowerCase().replace(URL_REPLACE_REGEXP, "_")}`);
		}

		if (filterState.yearFrom > 0) {
			newUrl.push(`yearFrom=${filterState.yearFrom}`);
		}

		if (filterState.moviesOnly) {
			newUrl.push("filmOrSeries=m");
		} else if (filterState.seriesOnly) {
			newUrl.push("filmOrSeries=s");
		}

		if (newUrl.length > 0) {
			console.debug("Updating url status", newUrl);
			router.replace(`?${newUrl.join("&")}`, undefined, { shallow: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterState]);

	const { fiLoading, fiData, fiLoaded } = useFilminfo(QUERY,
		{
			moviesOnly: filterState.moviesOnly,
			seriesOnly: filterState.seriesOnly,
			onlyContentWithPoster: true,
			genres: filterState.genres,
			providerNames: filterState.providers.map(p => p.name),
			yearFrom: filterState.yearFrom,
			yearTo: filterState.yearTo,
			maxNumTitles: 52
		},
		{
			active: filterState.activeSearch,
			cacheTime: 15 * 1000 * 60,
			staleTime: 15 * 1000 * 60
		});

	useEffect(() => {
		if (!fiLoading && fiLoaded && isReadyForScrollRestoration) {
			isReadyForScrollRestoration(true);
		}
	}, [fiLoading, fiLoaded, isReadyForScrollRestoration]);

	const buttonTexts = createButtonTexts(filterState);

	const data = prepareResultData(fiData?.streamingQuery?.getSuggestedStreamingContent as StreamingContent[] | null | undefined);

	return <>
		<SButtonContainer>
			<SFilterButton text={buttonTexts.genreText} onClick={() => filterDispatch({ type: "showOptionsGenre" })} title={buttonTexts.genreText} />
			<SFilterButton text={buttonTexts.periodText} onClick={() => filterDispatch({ type: "showOptionsPeriod" })} title={buttonTexts.periodText} />
			<SFilterButton text={buttonTexts.filmOrSeriesText} onClick={() => filterDispatch({ type: "showOptionsFilmOrSeries" })} title={buttonTexts.filmOrSeriesText} />
			<SFilterButton text={buttonTexts.providerText} onClick={() => filterDispatch({ type: "showOptionsProviders" })} title={buttonTexts.providerText} />
		</SButtonContainer>

		{filterState.showOptionsGenre && <OptionsGenre filterDispatch={filterDispatch} filterState={filterState} />}
		{filterState.showOptionsPeriod && <OptionsPeriod filterDispatch={filterDispatch} filterState={filterState} />}
		{filterState.showOptionsFilmOrSeries && <OptionsFilmOrSeries filterDispatch={filterDispatch} filterState={filterState} />}
		{filterState.showOptionsProviders && <OptionsProviders filterDispatch={filterDispatch} filterState={filterState} providers={providers} />}

		<SSpaceContainer aria-busy={fiLoading}>
			{fiLoading && <LoadingIndicator position="InlineCenter" />}
			{!fiLoading && fiLoaded && <>
				{(data?.top?.length ?? 0) === 0 && <p>Ingen titler matcher ditt filter</p>}
				{(data?.top?.length ?? 0) > 0 && <>
					<SLayout>
						{data!.top!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
					</SLayout>
					<SAd targetId="SG3_top_pop" height="300" />

					{(data?.mid1?.length ?? 0) > 0 && <SLayout>
						{data!.mid1!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
					</SLayout>}

					{(data?.mid2?.length ?? 0) > 0 && <>
						<SAd targetId="SG3_midt1_pop" height="300" />
						<SLayout>
							{data!.mid2!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
						</SLayout>
					</>}

					{(data?.mid3?.length ?? 0) > 0 && <>
						<SAd targetId="SG3_midt2_pop" height="300" />
						<SLayout>
							{data!.mid3!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
						</SLayout>
					</>}

					{(data?.mid4?.length ?? 0) > 0 && <>
						<SAd targetId="SG3_midt3_pop" height="300" />
						<SLayout>
							{data!.mid4!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
						</SLayout>
					</>}

					{(data?.rest?.length ?? 0) > 0 && <>
						<SAd targetId="SG3_midt4_pop" height="300" />
						<SLayout>

							{data!.rest!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
						</SLayout>
					</>}
				</>}
			</>}
		</SSpaceContainer>
	</>
}


export default withScrollRestoration(StreamingContentFinder);
//#endregion

const WLStreamingTitleWithProviders = withWatchlist(StreamingTitleWithProviders, "data");

const URL_REPLACE_REGEXP = /[-._~:/?#[\]@!$&'()*+,;= ]/g;

//#region [Styles]
const SButtonContainer = styled.div`
	margin: 0 ${theme.maxContentPadding};
`;

const SFilterButton = styled(Button)`
	max-width: 400px;
    cursor: pointer;
    background: ${theme.palette.sgGradient};
    color: ${theme.palette.lillaMørkHovedfarge};
    font-weight: bold;
    font-size: 20px;
    text-align: center;
	display: block;
	width: 100%;


	overflow: hidden;
	text-overflow: ellipsis;
	margin-bottom: 20px;
	@media ${theme.mq.desktop} {
		margin-left: auto !important;
    	margin-right: auto;
	}
`;

const SSpaceContainer = styled.div`
	min-height: 400px;
    color: var(--textcolor);
    text-align: center;

`;

const SLayout = styled.div`

	display: flex;
	flex-direction: column;
	gap: 10px;
	margin: 0 ${theme.maxContentPadding};


	@media ${theme.mq.desktop} {
		display: grid;
		grid-gap: 4%;
		gap: 50px;
		grid-template-columns: repeat(4, calc((100% - 150px) / 4));
		grid-auto-rows: auto;
		margin: 0;
	}
`;

const SAd = styled(Ad)`
	margin: 10px auto;
	@media ${theme.mq.desktop} {
		margin: 50px auto;
	}
`;

//#endregion

//#region [Other] Helpers
function parseInitialUrlState(router: ReturnType<typeof useRouter>, providers: StreamingProvider[], filterDispatch: Dispatch<FilterAction>) {
	// read state from url
	const query = router.query;
	if (Object.keys(query).length > 0) {
		console.debug("Parsing initial route");
		let doInitFilter = false;
		let genres: string[] = [];
		let _providers: StreamingProvider[] = [];
		let yearFrom = 0;
		let yearTo = 0;
		let moviesOnly = false;
		let seriesOnly = false;
		if (query.genres) {
			const urlGenres = ((query.genres as string).toLowerCase().match(/.{1,3}/g) ?? []) as string[];
			genres = STREAMING_GENRES.filter(sg => urlGenres.includes(sg.toLowerCase().substring(0, 3)));
			doInitFilter = doInitFilter || genres.length > 0;
		}

		if (query.providers) {
			const urlProviders = ((query.providers as string).toLowerCase().match(/.{1,4}/g) ?? []) as string[];
			_providers = providers.filter(p => urlProviders.includes(p.name!.substring(0, 4).toLowerCase().replace(URL_REPLACE_REGEXP, "_")))
			doInitFilter = doInitFilter || _providers.length > 0;
		}

		let paramYearFrom = query.yearFrom;
		if (paramYearFrom) {
			yearFrom = Number(paramYearFrom);
			switch (yearFrom) {
				case 1800:
					yearTo = 1979;
					break;
				case 1980:
					yearTo = 1989;
					break;
				case 1990:
					yearTo = 1999;
					break;
				case 2000:
					yearTo = 2009;
					break;
				case 2010:
					yearTo = 2019;
					break;
				case 2020:
					yearTo = 2029;
					break;
				default:
					yearFrom = yearTo = 0;
			}
			doInitFilter = doInitFilter || yearFrom > 0;
		}

		if (query.filmOrSeries) {
			if (query.filmOrSeries === "m") {
				moviesOnly = true;
			} else if (query.filmOrSeries === "s") {
				seriesOnly = true;
			}
			doInitFilter = doInitFilter || seriesOnly || moviesOnly;
		}


		if (doInitFilter) {
			console.debug("Updating filter state");
			filterDispatch({
				type: "preinitfilter",
				genres,
				providers: _providers,
				yearFrom: yearFrom > 0 ? yearFrom : 0,
				yearTo: yearTo > 0 ? yearTo : 0,
				moviesOnly,
				seriesOnly
			});
		}
	}
}

function createButtonTexts(filterState: IFindSomethingToStreamFilterState): {
	genreText: string;
	periodText: string;
	filmOrSeriesText: string;
	providerText: string;
} {
	let buttonGenreText = "Sjanger";
	if (filterState.genres?.length > 0) {
		buttonGenreText += ": " + filterState.genres;
	}

	let buttonPeriodText = "Periode";
	switch (filterState.yearFrom) {
		case 1800:
			buttonPeriodText += ": Før 1980";
			break;
		case 1980:
			buttonPeriodText += ": 80-tallet";
			break;
		case 1990:
			buttonPeriodText += ": 90-tallet";
			break;
		case 2000:
			buttonPeriodText += ": 2000-tallet";
			break;
		case 2010:
			buttonPeriodText += ": 2010-tallet";
			break;
		case 2020:
			buttonPeriodText += ": 2020-tallet";
			break;

	}

	let buttonFilmOrSeriesText = "Film/Serie";
	if (filterState.moviesOnly) {
		buttonFilmOrSeriesText += ": Kun film";
	} else if (filterState.seriesOnly) {
		buttonFilmOrSeriesText += ": Kun serie";
	}

	let buttonProviderText = "Streamingtjenester";
	if (filterState.providers?.length > 0) {
		buttonProviderText += ": " + filterState.providers.map(p => p.name).join(", ");
	}

	return {
		genreText: buttonGenreText,
		periodText: buttonPeriodText,
		filmOrSeriesText: buttonFilmOrSeriesText,
		providerText: buttonProviderText
	};
}

interface IPreparedData {
	top: StreamingContent[];
	mid1: StreamingContent[];
	mid2: StreamingContent[];
	mid3: StreamingContent[];
	mid4: StreamingContent[];
	rest: StreamingContent[];
}

function prepareResultData(streamingData: StreamingContent[] | undefined | null): IPreparedData | undefined {
	if ((streamingData?.length ?? 0) === 0) return undefined;
	return {
		top: streamingData!.slice(0, 4),
		mid1: streamingData!.slice(4, 12),
		mid2: streamingData!.slice(12, 20),
		mid3: streamingData!.slice(20, 28),
		mid4: streamingData!.slice(28, 36),
		rest: streamingData!.slice(36)
	};
}
//#endregion



//#region [Other] Query
const QUERY = gql`
query($moviesOnly: Boolean, $seriesOnly: Boolean, $onlyContentWithPoster: Boolean, $providerNames: [String], $genres: [String], $yearFrom: Int, $yearTo: Int, $maxNumTitles: Int!) {
	streamingQuery {
        getSuggestedStreamingContent(moviesOnly: $moviesOnly, seriesOnly: $seriesOnly, onlyContentWithPoster: $onlyContentWithPoster, providerNames: $providerNames, genres: $genres, yearFrom: $yearFrom, yearTo: $yearTo, maxNumItems: $maxNumTitles) {
			__typename
			id
			title
			created
			year
			isSeries
			popularity
			genres
			providerContents {
				__typename
				id
				provider {
					id
					logo
					name
				}
				title
				url
			}
			imagesPoster {
				height
				width
				url
			}
			imagesWide {
				height
				width
				url
			}
	  	}
	}
}
`;
//#endregion